// E34: links the 7-day calendar strip to the event list below it. Tapping a day
// cell scrolls the list to that day's event (or the nearest upcoming one).
//
// Dates are matched by LOCAL WALL-CLOCK day, consistent with the E32
// floating-local-time fix: an event's `start` may be a bare "YYYY-MM-DDTHH:mm"
// or a UTC "...Z" instant (the live server feed serialises the source
// wall-clock via new Date(local).toISOString()), so we reuse wallClockParts to
// pull the intended calendar day out timezone-independently. Using new Date()
// + getDate() here instead would shift the day across the date line on a
// non-UTC device, mapping a cell to the wrong event.

import { wallClockParts } from "./calendarLinks";
import { holdRouteScrollReset } from "./guidedTourScroll";

export interface DatedEvent {
  id: string;
  start: string;
  // E49: optional wall-clock end. When present, a tap on any day between the
  // start and end (inclusive) resolves to the event, so a day inside an ONGOING
  // multi-day event links to it rather than reading as empty.
  end?: string;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

// The event's local wall-clock day as "YYYY-MM-DD". This is the value used both
// for the day-cell match and for the event row's stable DOM id, so the two
// always agree.
export function eventLocalDate(event: { start: string }): string {
  const wc = wallClockParts(event.start);
  return `${wc.year}-${pad(wc.month)}-${pad(wc.day)}`;
}

// Stable, UNIQUE DOM id for an event row keyed by the event's own id. Shared by
// the rendering site (id={eventDomId(ev.id)}) and the day-cell tap lookup, so a
// strip tile lands on the EXACT event it resolves to. Keying by event id (not by
// local day) is deliberate: multiple events can start on the same wall-clock
// day, and a day-keyed id collides — document.getElementById then returns only
// the first such row, so an interior/ongoing tap (or a tap on the second event
// of a shared day) scrolled to the wrong card. Event ids are unique, so the
// strip tile always reaches its own event.
export function eventDomId(eventId: string): string {
  return `calendar-event-${eventId}`;
}

// Resolve which event a tapped day cell should scroll to.
//   - exact match: the first event whose local day equals isoDay,
//   - otherwise the nearest UPCOMING event strictly on/after isoDay (so an
//     empty day does not silently no-op, per the brief),
//   - null only when nothing is on/after that day.
// `events` need not be sorted; we pick the earliest qualifying day ourselves.
export function findScrollTargetEvent<T extends DatedEvent>(
  events: T[],
  isoDay: string,
): T | null {
  let exact: T | null = null;
  let nearest: T | null = null;
  let nearestDay: string | null = null;

  for (const ev of events) {
    const day = eventLocalDate(ev);
    if (day === isoDay) {
      // First exact match wins; keep scanning only to stay deterministic on
      // input order (callers pass display order, so "first" is the top row).
      if (exact === null) exact = ev;
      continue;
    }
    if (day > isoDay) {
      if (nearestDay === null || day < nearestDay) {
        nearestDay = day;
        nearest = ev;
      }
    }
  }

  return exact ?? nearest;
}

// E47: exact-only resolver for the day-cell TAP path. Unlike
// findScrollTargetEvent, an empty day returns null with NO nearest-upcoming
// fallback, so the tap can show a subtle "no events" cue instead of jumping to
// an unrelated day. Returns the first event (display order) whose local
// wall-clock day equals isoDay, else null.
export function findExactDayEvent<T extends DatedEvent>(
  events: T[],
  isoDay: string,
): T | null {
  for (const ev of events) {
    if (eventLocalDate(ev) === isoDay) return ev;
  }
  return null;
}

// E49: the event's local wall-clock END day as "YYYY-MM-DD", or null when the
// event has no `end`. Built with wallClockParts so it stays timezone-independent
// exactly like eventLocalDate.
export function eventEndLocalDate(event: { end?: string }): string | null {
  if (!event.end) return null;
  const wc = wallClockParts(event.end);
  return `${wc.year}-${pad(wc.month)}-${pad(wc.day)}`;
}

// E49: does the event span the given local day? True when the day is the start
// day, or (for an event with an `end`) falls strictly after the start day and on
// or before the end day. Single-day events (no `end`) only cover their start.
export function eventCoversDay(
  event: { start: string; end?: string },
  isoDay: string,
): boolean {
  const startDay = eventLocalDate(event);
  if (startDay === isoDay) return true;
  const endDay = eventEndLocalDate(event);
  if (endDay === null) return false;
  return isoDay > startDay && isoDay <= endDay;
}

// E49: day-cell TAP resolver that understands ONGOING events. An exact start-day
// match wins (so a day that both starts an event and sits inside another's run
// links to its own event); otherwise the first event whose [start, end] range
// covers the day. Returns null only when no event starts on or is ongoing
// through the day — the caller then does nothing (no misleading "no events"
// cue), since an empty-looking day may still sit between two unrelated events.
export function findActiveDayEvent<T extends DatedEvent>(
  events: T[],
  isoDay: string,
): T | null {
  let covering: T | null = null;
  for (const ev of events) {
    if (eventLocalDate(ev) === isoDay) return ev;
    if (covering === null && eventCoversDay(ev, isoDay)) covering = ev;
  }
  return covering;
}

// E48: accessible label for a week-strip day cell. The cell's visible content is
// just a narrow weekday + day number (+ artwork/dot), which a screen reader
// announces as a bare "S 21" with no hint that it links anywhere. This spells
// out the full weekday + date and, when the day hosts an event, names the event
// it links to ("Open <title> on Monday, Jun 22"). Empty days get a label that
// makes the no-op explicit ("No events on Monday, Jun 22") so the cell still
// reads as a meaningful, intentionally-inert control. `date` is the cell's local
// Date; `eventTitle` is the title of the event the cell shows, or null/empty.
export function dayCellAccessibleLabel(
  date: Date,
  eventTitle: string | null | undefined,
): string {
  const human = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
  const title = eventTitle?.trim();
  return title ? `Open ${title} on ${human}` : `No events on ${human}`;
}

/**
 * One-shot hand-off from another page to a Calendar event row (2026-09-04
 * owner ask: a Profile event card should land ON its event, not at the top
 * of the Calendar). The caller ARMS the id right before navigating; the
 * Calendar CONSUMES it on mount and scrolls once the row exists. Module
 * state, same pattern as the Dynamax grid's hand-off: it only lives inside
 * one in-app navigation, so a later plain visit opens the Calendar as usual.
 */
let pendingCalendarEventId: string | null = null;

/**
 * How long the route-change scroll reset stands down after a hand-off is
 * armed: past its 2 s scroll-event guard and the Calendar's last re-assert
 * at 1.8 s, so nothing re-zeros the page while the row is being landed.
 */
export const CALENDAR_HANDOFF_HOLD_MS = 2600;

export function armCalendarEvent(eventId: string): void {
  pendingCalendarEventId = eventId;
  holdRouteScrollReset(CALENDAR_HANDOFF_HOLD_MS);
}

export function consumeCalendarEvent(): string | null {
  const id = pendingCalendarEventId;
  pendingCalendarEventId = null;
  return id;
}
